import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Upload, FileText, X } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB

/* ---------------- Homepage Teaser ----------------
 * Replaces the full PolicyReview form on the homepage with a calm CTA block
 * that routes the user to the dedicated /policy-review page.
 * Visual treatment intentionally mirrors the surrounding sections.
 */
export const PolicyReviewTeaser = () => {
  return (
    <section
      id="policy-review"
      data-testid="policy-teaser-section"
      className="bg-paperdeep py-28 md:py-40"
    >
      <div className="max-w-[760px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <p className="eyebrow mb-7">Free Policy Review</p>
        <h2
          className="font-serif text-nearblack leading-[1.06] text-balance"
          style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", fontWeight: 400 }}
          data-testid="policy-teaser-headline"
        >
          Already have life insurance?
          <br />
          <em className="italic text-camel">Let&apos;s look at it together.</em>
        </h2>
        <p className="mt-9 text-nearblack/80 text-[16px] md:text-[17px] leading-[1.85] font-light max-w-[560px] mx-auto">
          Not sure what you own, how it works, or whether it supports your goals? Send me a copy
          and I&apos;ll give you an honest, educational review — complimentary and private.
        </p>
        <a
          href="/policy-review"
          data-testid="policy-teaser-cta"
          className="mt-12 btn-outline inline-flex items-center justify-center gap-2"
        >
          Request a Policy Review
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </section>
  );
};

/* ---------------- Full Policy Review section (form) ----------------
 * Unchanged in behavior. Rendered on the /policy-review page.
 */
export const PolicyReview = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [insuranceCompany, setInsuranceCompany] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const onPickFile = (selected) => {
    if (!selected) return;
    if (selected.size > MAX_FILE_BYTES) {
      toast.error("File is larger than 10MB. Please send a smaller version.");
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error("Please provide your name and email at minimum.");
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("first_name", firstName.trim());
      form.append("last_name", lastName.trim());
      form.append("email", email.trim());
      form.append("phone", phone.trim());
      form.append("insurance_company", insuranceCompany.trim());
      form.append("note", note.trim());
      if (file) form.append("file", file);
      await axios.post(`${API}/policy-review`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Received. Liz will be in touch within 2 business days.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="policy-review"
      data-testid={TEST_IDS.policy.section}
      className="bg-paperdeep py-28 md:py-40"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-7">Free Policy Review</p>
            <h2
              className="font-serif text-nearblack leading-[1.06] text-balance"
              style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", fontWeight: 400 }}
            >
              Already have life insurance?
              <br />
              <em className="italic text-camel">
                Let’s look at it together.
              </em>
            </h2>
            <p className="mt-8 text-nearblack/80 text-[16px] md:text-[17px] leading-[1.85] font-light max-w-md">
              Not sure what type of policy you own, how it works, or whether
              it aligns with your goals? Send me a copy and I’ll review what
              you have, how it functions, and whether it supports your
              overall financial strategy.
            </p>
            <p className="mt-6 text-nearblack/70 text-[14.5px] leading-[1.85] font-light max-w-md">
              Complimentary and private. Liz reviews each submission
              personally and reaches out within two business days.
            </p>

            <ul className="mt-10 space-y-3 max-w-md">
              {[
                "Understand exactly what you own",
                "See how the policy functions over time",
                "Identify gaps or misalignments",
                "Receive thoughtful, educational feedback",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 text-[14px] text-nearblack/75 font-light border-t border-camel/30 pt-3"
                >
                  <span className="block w-3 h-px bg-camel" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            {!done ? (
              <form
                onSubmit={handleSubmit}
                noValidate
                data-testid={TEST_IDS.policy.form}
                className="bg-paper border border-paperdeep p-8 md:p-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      data-testid={TEST_IDS.policy.firstName}
                      placeholder="Your first name"
                      disabled={loading}
                      className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      data-testid={TEST_IDS.policy.lastName}
                      placeholder="Your last name"
                      disabled={loading}
                      className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid={TEST_IDS.policy.email}
                      placeholder="you@email.com"
                      disabled={loading}
                      className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
                      Phone <span className="normal-case text-stonegrey/70 tracking-normal">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid={TEST_IDS.policy.phone}
                      placeholder="(555) 555-5555"
                      disabled={loading}
                      className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                    />
                  </div>
                </div>

                <div className="mt-7">
                  <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
                    Current Insurance Company <span className="normal-case text-stonegrey/70 tracking-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={insuranceCompany}
                    onChange={(e) => setInsuranceCompany(e.target.value)}
                    data-testid="policy-insurance-company"
                    placeholder="e.g. Northwestern Mutual, MassMutual, NYL…"
                    disabled={loading}
                    className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                  />
                </div>

                <div className="mt-7">
                  <label className="block label-small text-stonegrey mb-2" style={{ fontSize: "0.65rem" }}>
                    A few words about what you’d like reviewed <span className="normal-case text-stonegrey/70 tracking-normal">(optional)</span>
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid={TEST_IDS.policy.note}
                    placeholder="Anything you’d like me to know about this policy or your situation."
                    rows={3}
                    disabled={loading}
                    className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors resize-none"
                  />
                </div>

                <div className="mt-9">
                  <label className="block label-small text-stonegrey mb-3" style={{ fontSize: "0.65rem" }}>
                    Upload Your Policy <span className="text-camel">·</span> PDF, DOC, or image, up to 10MB <span className="normal-case text-stonegrey/70 tracking-normal">(optional)</span>
                  </label>
                  {!file ? (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid={`${TEST_IDS.policy.file}-trigger`}
                      className="w-full border border-dashed border-paperdeep hover:border-camel hover:bg-paper/50 transition-colors py-10 px-6 flex flex-col items-center gap-3 text-stonegrey"
                    >
                      <Upload className="w-5 h-5" strokeWidth={1.5} />
                      <span className="text-[13px] tracking-wide">
                        Click to upload your policy document
                      </span>
                    </button>
                  ) : (
                    <div
                      data-testid="policy-file-attached"
                      className="flex items-center justify-between border border-paperdeep px-5 py-4 bg-paper/60"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-4 h-4 text-camel shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0">
                          <p className="text-[13px] text-nearblack truncate">
                            {file.name}
                          </p>
                          <p className="text-[11px] text-stonegrey">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        aria-label="Remove file"
                        className="text-stonegrey hover:text-nearblack ml-3"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => onPickFile(e.target.files?.[0])}
                    data-testid={TEST_IDS.policy.file}
                    className="hidden"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid={TEST_IDS.policy.submit}
                  className="mt-10 w-full btn-outline inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Request Policy Review"}
                  {!loading && <ArrowRight className="w-3.5 h-3.5" />}
                </button>

                <p className="mt-5 text-[11px] text-stonegrey leading-relaxed">
                  Your document is sent privately to Liz. No third parties.
                </p>
              </form>
            ) : (
              <div
                data-testid={TEST_IDS.policy.success}
                className="bg-paper border border-camel/30 p-10 md:p-14 text-center"
              >
                <CheckCircle2
                  className="w-8 h-8 mx-auto text-camel"
                  strokeWidth={1.25}
                />
                <p className="mt-6 font-serif italic text-nearblack text-3xl md:text-4xl">
                  Thank you, {firstName.split(" ")[0]}.
                </p>
                <p className="mt-5 text-[15px] text-nearblack/75 leading-relaxed max-w-md mx-auto font-light">
                  Your policy has arrived. Liz reviews each submission
                  personally and will reach out to{" "}
                  <span className="text-nearblack">{email}</span> within two
                  business days.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
